import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api/api";
import { ISearchQueryParams } from "../redux/store.types";
import { getSearchQueryParams } from "../redux/store.utils";
import { useDispatchWrapper } from "./useDispatch";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../redux/messages/messagesActions";

interface UseTableDataProps {
  path: string;
  params: ISearchQueryParams;
}

interface UseTableDataReturn<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  totalRecords: number;
  refetch: () => void;
  upsert: (payload: Partial<T>, onCallback?: (isSuccess: boolean) => void) => Promise<boolean>;
}

export function useTableData<T = any>({ path, params }: UseTableDataProps): UseTableDataReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const dispatch = useDispatchWrapper();
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchData = useCallback(async () => {
    if (!path) return;

    setLoading(true);
    setError(null);
    try {
      const { signal } = paramsRef.current;
      const searchQuery = getSearchQueryParams(paramsRef.current);

      const res = await api.get(path, {
        params: searchQuery,
        signal,
      });

      const responseData: T[] = res.data.data;
      setData(responseData);
      setTotalRecords(
        res.data.totalRecords ?? res.data.total_records ?? res.data.total ?? responseData.length
      );
    } catch (err: any) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
      const message = err.response?.data?.message || "Failed to fetch data";
      setError(message);
      dispatch(
        showMessage({
          type: "error",
          message,
          displayAs: "snackbar",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [path, dispatch]);

  // Refetch on params change
  useEffect(() => {
    fetchData();
  }, [fetchData, JSON.stringify(params)]);

  const upsert = useCallback(
    async (payload: Partial<T>, onCallback?: (isSuccess: boolean) => void): Promise<boolean> => {
      try {
        dispatch(saveLoaderProgress());
        await api.post(path, payload);
        dispatch(
          showMessage({
            type: "success",
            message: "Saved successfully!",
            displayAs: "snackbar",
          })
        );
        await fetchData();
        onCallback?.(true);
        return true;
      } catch (err: any) {
        const message = err.response?.data?.message || "Failed to save";
        dispatch(
          showMessage({
            type: "error",
            message,
            displayAs: "snackbar",
          })
        );
        onCallback?.(false);
        return false;
      } finally {
        dispatch(saveLoaderCompleted());
      }
    },
    [path, dispatch, fetchData]
  );

  return { data, loading, error, totalRecords, refetch: fetchData, upsert };
}
